import React, { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Search, X, Loader2, Check } from 'lucide-react';
import { BadgeAlt, Input } from '@cargomatic/ui';
/**
 * Inline async autocomplete editor for use inside FilterChip popovers.
 * Does NOT wrap in its own Popover — renders search input + results directly
 * so nested-popover click-through bugs don't apply.
 *
 * Selected items render as removable badges at the top. Search results
 * render as a clickable list below.
 */
export function FilterAutocompleteEditor({ onSearch, toItem, value, labels, onAdd, onRemove, placeholder = 'Search...', minQueryLength = 2, debounceMs = 300, }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const reqId = useRef(0);
    const inputRef = useRef(null);
    const runSearch = useRef(debounce(async (q) => {
        const id = ++reqId.current;
        setLoading(true);
        try {
            const items = await onSearch(q);
            if (id === reqId.current)
                setResults(items);
        }
        catch (_a) {
            if (id === reqId.current)
                setResults([]);
        }
        finally {
            if (id === reqId.current)
                setLoading(false);
        }
    }, debounceMs)).current;
    useEffect(() => () => runSearch.cancel(), [runSearch]);
    const onQueryChange = useCallback((next) => {
        setQuery(next);
        setActiveIndex(-1);
        if (next.length >= minQueryLength) {
            runSearch(next);
        }
        else {
            runSearch.cancel();
            ++reqId.current;
            setLoading(false);
            setResults([]);
        }
    }, [minQueryLength, runSearch]);
    // Keyboard navigation:
    //   ↓/↑    move highlight through results
    //   Enter  add highlighted result (or first result if none highlighted)
    //   Backspace on empty query  remove last selected badge
    //   Esc    handled by parent Popover (closes)
    const onInputKeyDown = useCallback((e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, -1));
        }
        else if (e.key === 'Enter') {
            const idx = activeIndex >= 0 ? activeIndex : 0;
            const entity = results[idx];
            if (entity) {
                const item = toItem(entity);
                if (!value.includes(item.id)) {
                    onAdd(item.id, item.label);
                }
                runSearch.cancel();
                ++reqId.current;
                setQuery('');
                setActiveIndex(-1);
                setResults([]);
            }
        }
        else if (e.key === 'Backspace' && query === '' && value.length > 0) {
            e.preventDefault();
            onRemove(value[value.length - 1]);
        }
    }, [activeIndex, results, value, query, toItem, onAdd, onRemove]);
    return (React.createElement("div", { className: "space-y-3" },
        value.length > 0 && (React.createElement("div", { className: "flex flex-wrap gap-1 pb-2 border-b" }, value.map((id) => (React.createElement(BadgeAlt, { key: id, variant: "secondary", className: "flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs" },
            labels[id] || id,
            React.createElement("button", { type: "button", onClick: () => onRemove(id), className: "rounded-full p-0.5 hover:bg-destructive/10", "aria-label": `Remove ${labels[id] || id}` },
                React.createElement(X, { className: "size-3" }))))))),
        React.createElement("div", { className: "relative" },
            React.createElement(Search, { className: "absolute left-2 top-2 size-3.5 text-muted-foreground" }),
            React.createElement(Input, { ref: inputRef, value: query, onChange: (e) => onQueryChange(e.target.value), onKeyDown: onInputKeyDown, placeholder: placeholder, className: "h-8 pl-7 text-sm", autoFocus: true }),
            loading && (React.createElement(Loader2, { className: "absolute right-2 top-2 size-3.5 animate-spin text-muted-foreground" }))),
        React.createElement("div", { className: "max-h-48 overflow-y-auto space-y-0.5" },
            results.map((entity, idx) => {
                const item = toItem(entity);
                const selected = value.includes(item.id);
                const isActive = idx === activeIndex;
                return (React.createElement("button", { key: item.id, type: "button", onMouseEnter: () => setActiveIndex(idx), onClick: () => !selected && onAdd(item.id, item.label), className: `flex items-center gap-2.5 w-full px-2 py-1.5 rounded-md text-sm text-left ${selected
                        ? 'bg-accent cursor-default'
                        : isActive
                            ? 'bg-accent cursor-pointer'
                            : 'hover:bg-accent cursor-pointer'}` },
                    React.createElement("div", { className: "flex-1 min-w-0" },
                        React.createElement("div", { className: "font-medium truncate" }, item.label),
                        item.description && (React.createElement("div", { className: "text-xs text-muted-foreground truncate" }, item.description))),
                    selected && React.createElement(Check, { className: "size-3.5 text-primary shrink-0" })));
            }),
            query.length >= minQueryLength && !loading && results.length === 0 && (React.createElement("p", { className: "text-sm text-muted-foreground text-center py-3" }, "No results")),
            query.length < minQueryLength && (React.createElement("p", { className: "text-xs text-muted-foreground text-center py-3" },
                "Type at least ",
                minQueryLength,
                " characters to search")))));
}
